import React, { useState, useEffect } from 'react';
import { indirectEvalSync, getServerInstanceURL } from '@appblocks/utils';
import { Link } from 'react-router-dom';
import Table from 'react-bootstrap/Table';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import CodeMirror from '@uiw/react-codemirror';
import TextInput from '@appblocks/frontend/client/Components/TextInput';
import { autocompletion, startCompletion } from '@codemirror/autocomplete';
import {
    FaPen,
} from 'react-icons/fa';
import CodeMirrorWrapper from '@appblocks/frontend/client/Components/CodeMirrorWrapper';
import StringInput from '@appblocks/frontend/cloudclient/Components/StringInput';
import ExpressionBuilder from './ExpressionBuilder';
import ModelSelect from './ModelSelect';

export default function InputRenderer(props) {
    const {
        property,
        value,
        state,
        onChange,
        componentId,
        pageContext,
        properties,
        disabled,
        dataTestId,
    } = props;

    if (property.visible !== undefined) {
        const visibilityExpression = property.visible;
        const state = {
            properties,
        };
        const visible = indirectEvalSync(state, visibilityExpression);
        if (!visible) {
            return null;
        }
    }

    const renderInput = (property, value, disabled) => {
        try {
            switch (property.type) {
                case 'enum':
                    return (
                        <Form.Select
                            value={value}
                            onChange={e => onChange(property, e.target.value)}
                            disabled={disabled}
                            data-testid={`select-${dataTestId}`}
                        >
                            {property.options.map((option) => {
                                return (
                                    <option key={option.id} value={option.id}>{option.name}</option>
                                );
                            })}
                        </Form.Select>
                    );
                case 'code': {
                    const flattenObject = (obj, prefix = '') => {
                        return Object.keys(obj).reduce((acc, key) => {
                            const value = obj[key];
                            const newKey = prefix ? `${prefix}.${key}` : key;

                            if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
                                return acc.concat(newKey, flattenObject(value, newKey));
                            }

                            return acc.concat(newKey);
                        }, []);
                    };

                    const stateCompletions = (context) => {
                        const word = context.matchBefore(/\w*/);
                        if (word.from === word.to && !context.explicit) {
                            return null;
                        }
                        const text = context.state.doc.toString();
                        const posStart = text.indexOf('{{', 0);
                        const posEnd = text.indexOf('}}', context.pos);
                        if (posStart === -1 || posStart > context.pos || posEnd === -1) {
                            return null;
                        }

                        let options = flattenObject(pageContext)
                            .filter(option => !word || option.startsWith(word.text))
                            .sort((a, b) => {
                                const diff = a.length - b.length;
                                if (diff !== 0) return diff;

                                return a.localeCompare(b);
                            });
                        options = options.map((option, i) => {
                            return {
                                label: option,
                                apply: option,
                                boost: options.length - i,
                            };
                        });
                        return {
                            from: word.from,
                            options,
                        };
                    };

                    return (
                        <CodeMirrorWrapper
                            value={value.toString()}
                            basicSetup={{
                                lineWrapping: true,
                                scrollbarStyle: null,
                                lineNumbers: false,
                                autocompletion: true,
                                foldGutter: false,
                            }}
                            readOnly={disabled}
                            editable={!disabled}
                            extensions={[
                                // tokens,
                                autocompletion({
                                    closeOnBlur: true,
                                    override: [stateCompletions],
                                    selectOnOpen: props.autocomplete,
                                }),
                            ]}
                            language="javascript"
                            height="200px"
                            onChange={(value, viewUpdate) => {
                                onChange(property, value);
                            }}
                        />
                    );
                }
                case 'password':
                    return (
                        <Form.Control
                            type="password"
                            placeholder={property.name}
                            value={value}
                            disabled={disabled}
                            onChange={e => onChange(property, e.target.value)}
                        />
                    );
                case 'array': {
                    let columns = property.format;
                    if (typeof columns === 'object') {
                        columns = Object.keys(columns).map((key) => {
                            return {
                                key,
                                ...columns[key],
                            };
                        });
                    }
                    const totalColumns = columns.length;
                    const renderRow = (item, index) => {
                        return (
                            <tr key={index}>
                                {columns.map((formatItem, key) => {
                                    if (formatItem.options !== undefined) {
                                        return (
                                            <td key={`${index}-${key}`}>
                                                <Form.Select
                                                    value={item[formatItem.key]}
                                                    onChange={(e) => {
                                                        const tmp = e
                                                            .target.value;
                                                        const tmpValue = value
                                                            .map((item, i) => {
                                                                if (i === index) {
                                                                    return {
                                                                        ...item,
                                                                        [key]: tmp,
                                                                    };
                                                                }
                                                                return item;
                                                            });
                                                        onChange(
                                                            property, tmpValue,
                                                        );
                                                    }}
                                                >
                                                    {formatItem.options.map((option) => {
                                                        return (
                                                            <option
                                                                key={option
                                                                    .value}
                                                                value={option
                                                                    .value}
                                                            >
                                                                {option.name}
                                                            </option>
                                                        );
                                                    })}
                                                </Form.Select>
                                            </td>
                                        );
                                    }
                                    return (
                                        <td key={`${index}-${key}`}>
                                            <StringInput
                                                dataTestid={`input-${dataTestId}-${index}-${key}`}
                                                componentId={componentId}
                                                completionContext={pageContext}
                                                property={property}
                                                value={item[formatItem.key]}
                                                disabled={disabled}
                                                onChange={(newValue) => {
                                                    const tmpValue = value
                                                        .map((item, i) => {
                                                            if (i === index) {
                                                                return {
                                                                    ...item,
                                                                    [formatItem.key]: newValue,
                                                                };
                                                            }
                                                            return item;
                                                        });
                                                    onChange(property, tmpValue);
                                                }}
                                            />
                                        </td>
                                    );
                                })}
                                <td>
                                    <Button
                                        variant="danger"
                                        size="sm"
                                        disabled={disabled}
                                        onClick={() => {
                                            const tmpValue = value
                                                .filter((item, i) => i !== index);
                                            onChange(property, tmpValue);
                                        }}
                                    >
                                        X
                                    </Button>
                                </td>
                            </tr>
                        );
                    };

                    return (
                        <div>
                            {value.length > 0 && (
                                <table className="table table-sm">
                                    <thead>
                                        <tr>
                                            {columns.map((item, key) => {
                                                const columns = item.columns || 1;
                                                return (
                                                    <th
                                                        key={key}
                                                        style={{
                                                            width: `${(columns / totalColumns) * 100}%`,
                                                        }}
                                                    >
                                                        {item.key}
                                                    </th>
                                                );
                                            })}
                                            <th />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {value.map((item, index) => renderRow(item, index))}
                                    </tbody>
                                </table>
                            )}
                            <Button
                                variant="primary"
                                size="sm"
                                onClick={() => {
                                    const newItem = {};
                                    columns.forEach((property, key) => {
                                        newItem[property.key] = property.default || '';
                                    });
                                    const newValue = [
                                        ...value,
                                        newItem,
                                    ];
                                    onChange(property, newValue);
                                }}
                                disabled={disabled}
                            >
                                Add
                            </Button>
                        </div>
                    );
                }
                case 'object':
                    switch (property.control) {
                        case 'expression':
                            return (
                                <ExpressionBuilder
                                    dataTestId={`input-${dataTestId}`}
                                    properties={pageContext}
                                    property={property}
                                    value={value}
                                    state={state}
                                    disabled={disabled}
                                    onChange={onChange}
                                />
                            );
                        case 'url':
                            return (
                                <TextInput
                                    value={`${getServerInstanceURL()}/cgg/webhooks/${value}`}
                                    disabled
                                    showCopy
                                />
                            );
                        default:
                            return (
                                <StringInput
                                    dataTestid={`input-${dataTestId}`}
                                    componentId={componentId}
                                    completionContext={pageContext}
                                    property={property}
                                    value={value.toString()}
                                    disabled={disabled}
                                    onChange={value => onChange(property, value)}
                                />
                            );
                    }
                case 'model':
                    return (
                        // <div>Model</div>
                        <ModelSelect
                            model={property.model}
                            isDisabled={disabled}
                            onChange={value => onChange(property, value)}
                            value={value}
                        />
                    );
                case 'string':
                default:
                    return (
                        <StringInput
                            dataTestid={`input-${dataTestId}`}
                            componentId={componentId}
                            completionContext={pageContext}
                            property={property}
                            disabled={disabled}
                            value={value ? value.toString() : ''}
                            onChange={value => onChange(property, value)}
                        />
                    );
            }
        } catch (e) {
            console.log(e);
            return <p>Error Rendering Element</p>;
        }
    };

    return (
        <Form.Group className="mb-3">
            <Form.Label>{property.name}</Form.Label>
            {renderInput(property, value, disabled)}
        </Form.Group>
    );
}
